import React from 'react';
import { Badge } from '@ai-content-hub/content-kit';
import { Check, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLocale } from '../hooks/useLocale';
import type { I18nConfig } from '../schema';

export interface ScoreDisplayProps {
  score: number;
  max: number;
  showPercentage?: boolean;
  showFraction?: boolean;
  isCorrect?: boolean | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-xl',
};

/**
 * ScoreDisplay - Shows score information
 */
export function ScoreDisplay({
  score,
  max: maxPoints,
  showPercentage = true,
  showFraction = true,
  isCorrect,
  size = 'md',
  className,
}: ScoreDisplayProps): React.ReactElement {
  const percentage = maxPoints > 0 ? (score / maxPoints) * 100 : 0;

  return (
    <div className={cn('score-display', 'flex items-center gap-2', sizeClasses[size], className)}>
      {isCorrect !== null && isCorrect !== undefined && (
        <span
          className={cn(
            'score-icon w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
            isCorrect ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'
          )}
        >
          {/* SVG, not '✓'/'✕' glyphs: no glyph in the iOS system font -> tofu box in WebView */}
          {isCorrect ? <Check className="size-3.5" strokeWidth={3} /> : <X className="size-3.5" strokeWidth={3} />}
        </span>
      )}

      <div className="flex items-baseline gap-1">
        {showFraction && (
          <span className="font-bold">
            {score} <span className="text-muted-foreground">/</span> {maxPoints}
          </span>
        )}

        {showPercentage && (
          <span className={cn('text-muted-foreground', showFraction && 'text-sm')}>
            ({percentage.toFixed(0)}%)
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Compact score badge
 */
export function ScoreBadge({
  score,
  max: maxPoints,
  isCorrect,
  className,
}: Pick<ScoreDisplayProps, 'score' | 'max' | 'isCorrect' | 'className'>): React.ReactElement {
  const percentage = maxPoints > 0 ? (score / maxPoints) * 100 : 0;

  return (
    <Badge
      variant="ghost"
      className={cn(
        'score-badge gap-1 rounded-full border-transparent',
        isCorrect === true && 'bg-success/20 text-success',
        isCorrect === false && 'bg-destructive/20 text-destructive',
        isCorrect === null && 'bg-muted text-muted-foreground',
        className
      )}
    >
      <span className="font-bold">{percentage.toFixed(0)}%</span>
    </Badge>
  );
}

/**
 * Circular progress ring (pure SVG with inline stroke styles for theme compat)
 */
const RING_RADIUS = 15.5;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function CircularProgress({
  percentage,
  isPassing,
}: {
  percentage: number;
  isPassing: boolean;
}) {
  const offset = RING_CIRCUMFERENCE * (1 - Math.min(percentage, 100) / 100);
  const activeColor = isPassing
    ? 'hsl(var(--success))'
    : 'hsl(var(--destructive))';

  return (
    <div className="score-summary-ring relative shrink-0" style={{ width: 80, height: 80 }}>
      <svg viewBox="0 0 36 36" className="w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx="18"
          cy="18"
          r={RING_RADIUS}
          fill="none"
          style={{ stroke: 'hsl(var(--muted))' }}
          strokeWidth="3.5"
        />
        <circle
          cx="18"
          cy="18"
          r={RING_RADIUS}
          fill="none"
          style={{
            stroke: activeColor,
            strokeDasharray: RING_CIRCUMFERENCE,
            strokeDashoffset: offset,
            transition: 'stroke-dashoffset 0.8s ease-out',
          }}
          strokeWidth="3.5"
          strokeLinecap="round"
        />
      </svg>
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center text-lg font-bold',
          isPassing ? 'text-success' : 'text-destructive'
        )}
      >
        {percentage.toFixed(0)}%
      </span>
    </div>
  );
}

function getScoreLabel(percentage: number, t: (key: string, fallback?: string) => string): string {
  if (percentage >= 100) return t('scorePerfect', 'Perfect!');
  if (percentage >= 80) return t('scoreGreat', 'Great job!');
  if (percentage >= 60) return t('scoreGood', 'Good effort!');
  if (percentage >= 40) return t('scorePractice', 'Keep practicing!');
  return t('scoreTryAgain', 'Try again!');
}

/**
 * Score summary card with circular progress, stats, and progress bar
 */
export interface ScoreSummaryProps {
  totalScore: number;
  max: number;
  correctCount: number;
  totalCount: number;
  className?: string;
  /** Content i18n config for translations */
  i18nConfig?: I18nConfig;
  /** Language override */
  language?: string;
}

export function ScoreSummary({
  totalScore,
  max: maxPoints,
  correctCount,
  totalCount,
  className,
  i18nConfig,
  language,
}: ScoreSummaryProps): React.ReactElement {
  const { t } = useLocale({ config: i18nConfig, initialLanguage: language });
  const pointPercentage = maxPoints > 0 ? (totalScore / maxPoints) * 100 : 0;
  const countPercentage = totalCount > 0 ? (correctCount / totalCount) * 100 : 0;
  const percentage = totalCount > 0 ? countPercentage : pointPercentage;
  const isPassing = percentage >= 60;

  const scoreResultText = t('scoreResult', 'You got {correct} of {total} correct')
    .replace('{correct}', String(correctCount))
    .replace('{total}', String(totalCount));

  return (
    // EAA-1679: data-slot="card" on the div, not the kit Card — this summary
    // is often shown inside an already-bordered wrapper (ContentPlayer's
    // content-player-result block); a nested Card border stacks visibly.
    <div
      data-slot="card"
      className={cn(
        'score-summary rounded-xl border border-border shadow-sm p-2',
        className
      )}
    >
      {/* Hero: ring + label */}
      <div className="score-summary-hero flex items-center gap-4">
        <CircularProgress percentage={percentage} isPassing={isPassing} />

        <div className="flex-1 min-w-0">
          <h4
            className={cn(
              'score-summary-label text-lg font-bold',
              isPassing ? 'text-success' : 'text-destructive'
            )}
          >
            {getScoreLabel(percentage, t)}
          </h4>
          <p className="text-sm text-muted-foreground mt-1">
            {scoreResultText}
          </p>
        </div>
      </div>
    </div>
  );
}
